/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

import {
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  Grid, IconButton, Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import DoneIcon from "@material-ui/icons/Done";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";

import useTranslation from "src/translations/useTranslation";
import {
  CoordinateAnalysisMessages, GlobalMessages, MapMessages, TravelAnalysisMessages
} from "@shared/languages/interfaces";

import { MapColors } from "@shared/constants/map.enum";
import { VehicleStatesTypesColors, VehicleStatesTypesID } from "@shared/constants/vehicle-states-types.enum";
import { LocationTypes } from "@shared/constants/location-types.enum";

import { CircleOptions, CommandPointer, MarkerOptions, PolylineOptions } from "@shared/interfaces/map.interface";
import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";

import styled, { createGlobalStyle } from "styled-components";

import { MapAnalytics } from "@components/Map";
import TimeLineVehicleStates from "@components/TimeLines/TimeLineVehicleStates";

import utils from "@utils/useful-functions";
import { hasValidCoordinates } from "@utils/coordinates";
import useTravelAnalysisCoordinateView from "../../hooks/useTravelAnalysisCoordinateView";

/** Hides the default Google Maps InfoWindow close button and header. */
const MapWrapper = styled.div`
  height: 100%;
  .gm-style-iw-c {
    padding: 14px !important;
  }
  .gm-style-iw-ch,
  .gm-ui-hover-effect {
    display: none !important;
  }
`;

/** Forces MUI Tooltip to render above the fullscreen overlay (z-index 2000). */
const GlobalTooltipZIndex = createGlobalStyle`
  .MuiTooltip-popper {
    z-index: 2100 !important;
  }
`;

// region Map Helper Functions (replicated from CoordinateAnalysisMap — pure functions)

interface MapFilter {
  [key: string]: {
    class: string;
    title: string;
    status: boolean;
    color: string;
  };
}

interface InfoWindowLabels {
  route: string;
  outwardTrip: string;
  returnTrip: string;
  type: string;
  start: string;
  end: string;
  duration: string;
  speed: string;
}

/** Builds an inline-styled HTML string for Google Maps InfoWindow (no external CSS needed). */
// eslint-disable-next-line max-len
const buildInfoContent = (idx: number, rec: TravelAnalytics, going: boolean, labels: InfoWindowLabels): string => {
  const rs = "font-size:12px;padding:2px 0;";
  const ls = "font-weight:600;color:#424242;";
  const vs = "color:#616161;margin-left:4px;";
  const hr = "<hr style=\"margin:4px 0;border:none;border-top:1px solid #e0e0e0;\"/>";

  const routeLabel = going ? labels.outwardTrip : labels.returnTrip;
  // eslint-disable-next-line max-len
  let rows = `<div style="${rs}"><span style="${ls}">${labels.route}:</span><span style="${vs}">${routeLabel}</span></div>`;

  if (rec.vehicleState) {
    rows += hr;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.type}:</span><span style="${vs}">${rec.vehicleState.status.description}</span></div>`;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.start}:</span><span style="${vs}">${utils.formatDateIfHave(rec.vehicleState.registration_date, "fullDate")}</span></div>`;
    if (rec.vehicleState.finish_date) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.end}:</span><span style="${vs}">${utils.formatDateIfHave(rec.vehicleState.finish_date, "fullDate")}</span></div>`;
    }
    if (rec.vehicleState.duration) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.duration}:</span><span style="${vs}">${utils.formatDateIfHave(rec.vehicleState.duration, "durationDescriptiveTime")}</span></div>`;
    }
  }

  if (rec.alert) {
    rows += hr;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.type}:</span><span style="${vs}">${rec.alert.type.description}</span></div>`;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.start}:</span><span style="${vs}">${utils.formatDateIfHave(rec.alert.occurrence_date, "fullDate")}</span></div>`;
    if (rec.alert?.event?.finish_date) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.end}:</span><span style="${vs}">${utils.formatDateIfHave(rec.alert.event.finish_date, "fullDate")}</span></div>`;
    }
  }

  if (rec.event) {
    rows += hr;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.type}:</span><span style="${vs}">${rec.event.type.description}</span></div>`;
    // eslint-disable-next-line max-len
    rows += `<div style="${rs}"><span style="${ls}">${labels.start}:</span><span style="${vs}">${utils.formatDateIfHave(rec.event.start_date, "fullDate")}</span></div>`;
    if (rec.event.finish_date) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.end}:</span><span style="${vs}">${utils.formatDateIfHave(rec.event.finish_date, "fullDate")}</span></div>`;
    }
    if (rec.event.speed) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.speed}:</span><span style="${vs}">${Math.floor(rec.event.speed)} KM/h</span></div>`;
    }
  }

  if (rec.content?.SPD) {
    const spd = parseInt(rec.content.SPD.content, 10);

    if (!isNaN(spd)) {
      // eslint-disable-next-line max-len
      rows += `<div style="${rs}"><span style="${ls}">${labels.speed}:</span><span style="${vs}">${spd} KM/h</span></div>`;
    }
  }

  return `
    <div style="min-width:180px;font-family:Roboto,Arial,sans-serif;padding:4px;">
      <strong style="font-size:13px;">[${idx}] ${utils.formatDateIfHave(rec.occurrence_date, "fullDate")}</strong>
      <hr style="margin:6px 0;border:none;border-top:1px solid #e0e0e0;"/>
      ${rows}
    </div>
  `;
};

const generateMarkerOptions = (records: TravelAnalytics[], labels: InfoWindowLabels): MarkerOptions[] => {
  const generatedMarkers: MarkerOptions[] = [];

  // Drop records with corrupted/non-numeric coordinates ("S/N" from truncated tracker
  // packets) so building markers never reaches new LatLng(NaN) and crashes the view
  const validRecords = records.filter(hasValidCoordinates);

  if (validRecords.length === 0) return generatedMarkers;

  let isGoing = true;
  let index = 0;

  for (let i = 0; i < validRecords.length; i++) {
    const rec = validRecords[i];

    index += 1;

    const color: string = isGoing ? MapColors.GOING : MapColors.RETURNING;
    const generateTitle = (idx: number, description = "") =>
      `[${idx}] ${description}\n${utils.formatDateIfHave(rec.registration_date, "fullDate")}`;

    const infoContent = buildInfoContent(index, rec, isGoing, labels);

    const defaultOptions: MarkerOptions = {
      position: {
        lat: Number.parseFloat(rec?.content?.LAT?.content),
        lng: Number.parseFloat(rec?.content?.LON?.content)
      },
      title: generateTitle(index),
      opacity: 1,
      icon: {
        url: utils.getMarkerDynamically(Number.parseFloat(rec?.content?.CRS?.content ?? "0"), color),
        scaledSize: { width: 14, height: 14 } as google.maps.Size
      },
      visible: true,
      infoContent,
      class: isGoing ? "going" : "returning"
    };

    if (rec.vehicleState) {
      if (VehicleStatesTypesID.RETORNANDO === rec.vehicleState.status.id_vehicle_state_type) {
        isGoing = false;
      }
      generatedMarkers.push({
        ...defaultOptions,
        title: generateTitle(index, rec.vehicleState.status.description),
        animation: 2,
        opacity: 1,
        icon: {
          ...defaultOptions.icon as google.maps.Icon,
          url: utils.getMarkerAccordingVehicleState(rec.vehicleState.status.id_vehicle_state_type),
          scaledSize: { width: 30, height: 45 } as google.maps.Size
        },
        infoContent,
        class: "states"
      });
    } else if (rec.alert) {
      generatedMarkers.push({
        ...defaultOptions,
        title: generateTitle(index, rec.alert.type.description),
        animation: 2,
        opacity: 1,
        icon: {
          ...defaultOptions.icon as google.maps.Icon,
          url: utils.getMarkerAccordingVehicleAlert(rec.alert.type.id_alert_type),
          scaledSize: { width: 25, height: 40 } as google.maps.Size
        },
        infoContent,
        class: "alerts"
      });
    } else if (rec.event) {
      const markerOptions = {
        ...defaultOptions,
        title: generateTitle(index, rec.event.type.description),
        animation: 2,
        opacity: 1,
        icon: {
          ...defaultOptions.icon as google.maps.Icon,
          url: utils.getMarkerAccordingVehicleEvent(rec.event.type.id_event_type)
            || `http://chart.apis.google.com/chart?chst=d_map_pin_letter&chld=${index}|${"#ff9800".replace("#", "")}|000000`,
          scaledSize: { width: 25, height: 40 } as google.maps.Size
        },
        infoContent,
        class: "events"
      };

      if (rec.event.latitude && rec.event.longitude) {
        markerOptions.position = { lat: Number(rec.event.latitude), lng: Number(rec.event.longitude) };
      }
      generatedMarkers.push(markerOptions);
    } else {
      generatedMarkers.push(defaultOptions);
    }
  }

  return generatedMarkers;
};

const generateCirclesOptions = (records: TravelAnalytics[]): CircleOptions[] => {
  const circles: CircleOptions[] = [];

  records.forEach((rec) => {
    if (rec.vehicleState && rec.vehicleState.location) {
      circles.push({
        strokeColor: rec.vehicleState.location.type.description === LocationTypes.USINA
          ? VehicleStatesTypesColors.NA_USINA : VehicleStatesTypesColors.EM_OBRA,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: rec.vehicleState.location.type.description === LocationTypes.USINA
          ? VehicleStatesTypesColors.NA_USINA : VehicleStatesTypesColors.EM_OBRA,
        fillOpacity: 0.35,
        center: {
          lat: Number.parseFloat(rec.vehicleState.location.latitude),
          lng: Number.parseFloat(rec.vehicleState.location.longitude)
        },
        radius: Number.parseFloat(`${rec.vehicleState.location.radius}`) || 80
      });
    }
  });

  return circles;
};

const generatePolylinesOptions = (markerOptions: MarkerOptions[]): PolylineOptions[] => {
  interface Coord { lat: number; lng: number }
  interface PolylineCoord { going: Coord[]; returning: Coord[] }

  let isGoing = true;
  const generatedPolylines: PolylineOptions[] = [];
  const coords: PolylineCoord = { going: [], returning: [] };

  markerOptions.forEach((rec) => {
    if (isGoing && rec.class === "returning") isGoing = false;
    coords[`${isGoing ? "going" : "returning"}`].push(rec.position as Coord);
  });

  if (coords["returning"]?.length > 0) {
    coords["returning"].unshift(coords["going"][coords["going"].length - 1]);
  }

  const defaultOptions: PolylineOptions = { strokeOpacity: 0.8, strokeWeight: 8, geodesic: true };

  Object.keys(coords).forEach((key) => {
    generatedPolylines.push({
      ...defaultOptions,
      path: coords[key],
      strokeColor: key === "going" ? MapColors.GOING : MapColors.RETURNING,
      class: key
    });
  });

  return generatedPolylines;
};

const generateDefaultMapFilter = (t: any): MapFilter => ({
  going: { class: "going", title: "Ida", status: true, color: MapColors.GOING },
  returning: { class: "returning", title: "Volta", status: true, color: MapColors.RETURNING },
  states: { class: "states", title: t(MapMessages.states), status: true, color: "#7e73f8" },
  alerts: { class: "alerts", title: t(MapMessages.alerts), status: true, color: "#fd0013" },
  events: { class: "events", title: t(MapMessages.events), status: true, color: "#a89423" }
});

// endregion

/** Props for the TravelCoordinateViewModal. */
interface TravelCoordinateViewModalProps {
  open: boolean;
  onClose: () => void;
  travelId: string;
  vehicleDescription: string;
  numDoc?: string | null;
}

/**
 * Fullscreen overlay that displays the coordinate analysis view for a single travel.
 * Layout: States timeline (left) | Map (center) | Numbered commands list (right).
 * Opened from AlertsDrilldownModal via a button per row.
 */
const TravelCoordinateViewModal: React.FC<TravelCoordinateViewModalProps> = ({
  open,
  onClose,
  travelId,
  vehicleDescription,
  numDoc
}) => {
  const { t } = useTranslation();
  const { coords, loading, fetchCoordinateView, reset } = useTravelAnalysisCoordinateView();

  const [mapFilter, setMapFilter] = useState<MapFilter>(generateDefaultMapFilter(t));
  const [commandsPointer, setCommandsPointer] = useState<CommandPointer>({} as CommandPointer);
  const [commandDetail, setCommandDetail] = useState<{ record: TravelAnalytics; tab: "general" | "content" | "result" } | null>(null);

  // Fetch data when opened
  useEffect(() => {
    if (open && travelId) {
      fetchCoordinateView(travelId);
    }
    if (!open) {
      reset();
      setCommandsPointer({} as CommandPointer);
    }
  }, [open, travelId]);

  // Map data memos
  const infoLabels: InfoWindowLabels = useMemo(() => ({
    route: t(MapMessages.route),
    outwardTrip: t(MapMessages.outwardTrip),
    returnTrip: t(MapMessages.returnTrip),
    type: t(GlobalMessages.type),
    start: t(GlobalMessages.start),
    end: t(GlobalMessages.end),
    duration: t(GlobalMessages.duration),
    speed: t(GlobalMessages.speed)
  }), [t]);
  const markers = useMemo(() => generateMarkerOptions(coords, infoLabels), [coords, infoLabels]);
  const polylines = useMemo(() => generatePolylinesOptions(markers), [markers]);
  const circles = useMemo(() => generateCirclesOptions(coords), [coords]);

  // Build vehicle mock for TimeLineVehicleStates
  const vehicleForTimeline = useMemo((): Vehicle => {
    const states = coords.filter((r) => !!r.vehicleState).map((r) => r.vehicleState);
    const alerts = coords.filter((r) => !!r.alert).map((r) => r.alert);

    return { states, alerts } as unknown as Vehicle;
  }, [coords]);

  const mapFilterChips = useMemo(() => Object.keys(mapFilter).map((key) => {
    const filter = mapFilter[key];

    return (
      <Chip
        key={`cv-filter-${filter.title}`}
        label={filter.title}
        style={{
          minWidth: "80px",
          fontWeight: "bold",
          margin: "2px 4px",
          color: filter.status ? "#fafafa" : "#9e9e9e",
          backgroundColor: filter.status ? filter.color : "#DDDDDD"
        }}
        onDelete={() => {
          setMapFilter((state) => ({ ...state, [key]: { ...state[key], status: !filter.status } }));
        }}
        {...(!filter.status ? { deleteIcon: <DoneIcon /> } : {})}
      />
    );
  }), [mapFilter]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      zIndex: 2000,
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column"
    }}>
      <GlobalTooltipZIndex />
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "10px 16px",
        borderBottom: "1px solid #e0e0e0",
        flexShrink: 0
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "16px" }}>
            {t(TravelAnalysisMessages.coordinateViewTitle)}
          </div>
          <div style={{ fontSize: "13px", color: "#757575", marginTop: "2px" }}>
            {t(TravelAnalysisMessages.coordinateViewVehicle)}: {vehicleDescription}
          </div>
          {numDoc && (
            <div style={{ fontSize: "13px", color: "#757575", marginTop: "1px" }}>
              {t(TravelAnalysisMessages.coordinateViewNF)}: {numDoc}
            </div>
          )}
        </div>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <CircularProgress size={48} />
        </div>
      ) : coords.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, color: "#999" }}>
          {t(CoordinateAnalysisMessages.noCommandsForThisPeriod)}
        </div>
      ) : (
        <Grid container style={{ flex: 1, overflow: "hidden" }}>
          {/* Left column — States + Alerts timeline */}
          <Grid item xs={3} style={{ height: "100%", overflow: "auto", borderRight: "1px solid #e0e0e0", padding: "8px", paddingTop: "16px" }}>
            <TimeLineVehicleStates vehicle={vehicleForTimeline} historicalTravel />
          </Grid>

          {/* Center column — Map */}
          <Grid item xs={6} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <MapWrapper>
              <MapAnalytics
                mapHeight="100%"
                markers={markers}
                polylines={polylines}
                circles={circles}
                snapToRoads
                commandsPointer={commandsPointer}
                filter={Object.keys(mapFilter).map((key) => ({
                  class: mapFilter[key].class,
                  status: mapFilter[key].status
                }))}
              />
            </MapWrapper>
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", padding: "4px 0", flexShrink: 0 }}>
              {mapFilterChips}
            </div>
          </Grid>

          {/* Right column — Numbered commands list */}
          <Grid item xs={3} style={{ height: "100%", overflow: "auto", borderLeft: "1px solid #e0e0e0", padding: "8px", paddingTop: "16px" }}>
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>
              {t(CoordinateAnalysisMessages.commands)} ({coords.length})
            </div>
            {coords.map((record, index) => (
              <div
                key={record.binary_data || `cmd-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 6px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: "12px"
                }}
              >
                <Chip label={index + 1} size="small" style={{ minWidth: "32px", height: "20px" }} />
                <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {utils.formatDateIfHave(new Date(record.occurrence_date), "fullDate")}
                </span>
                <span style={{ display: "flex", alignItems: "center", flexShrink: 0, gap: "2px" }}>
                  {record.vehicleState && (
                    <Tooltip title={record.vehicleState.status.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleState(record.vehicleState.status.id_vehicle_state_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {record.alert && (
                    <Tooltip title={record.alert.type.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleAlert(record.alert.type.id_alert_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {record.event && (
                    <Tooltip title={record.event.type.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleEvent(record.event.type.id_event_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {!record.vehicleState && !record.alert && !record.event && <RemoveIcon style={{ fontSize: 14 }} />}
                </span>
                <span style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewGeneral)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "general" })}>
                      <InfoOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewContent)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "content" })}>
                      <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewResult)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "result" })}>
                      <AssessmentOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(CoordinateAnalysisMessages.showMarker)} arrow>
                    <IconButton size="small" onClick={() => setCommandsPointer({ ...commandsPointer, selected: index })}>
                      <RoomOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Command Detail Dialog */}
      <CommandDetailDialog
        detail={commandDetail}
        onClose={() => setCommandDetail(null)}
      />
    </div>
  );
};

// region Command Detail Dialog

interface CommandDetailDialogProps {
  detail: { record: TravelAnalytics; tab: "general" | "content" | "result" } | null;
  onClose: () => void;
}

const TAB_TITLES: Record<string, TravelAnalysisMessages> = {
  general: TravelAnalysisMessages.coordinateViewGeneralData,
  content: TravelAnalysisMessages.coordinateViewCommandContent,
  result: TravelAnalysisMessages.coordinateViewProcessingResult
};

/** Renders a key-value table from an object. Handles nested objects gracefully. */
const renderObjectTable = (obj: Record<string, any> | null | undefined, t: (key: string) => string) => {
  if (!obj || Object.keys(obj).length === 0) {
    return <div style={{ color: "#999", padding: "8px 0" }}>{t(TravelAnalysisMessages.coordinateViewNoData)}</div>;
  }

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
      <thead>
        <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>{t(TravelAnalysisMessages.coordinateViewField)}</th>
          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>{t(TravelAnalysisMessages.coordinateViewValue)}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(obj).map(([key, value]) => (
          <tr key={key} style={{ borderBottom: "1px solid #f0f0f0" }}>
            <td style={{ padding: "4px 8px", fontWeight: 500, color: "#424242", verticalAlign: "top", whiteSpace: "nowrap" }}>
              {key}
            </td>
            <td style={{ padding: "4px 8px", color: "#616161", wordBreak: "break-all" }}>
              {formatCellValue(value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** Format a single cell value for display. */
const formatCellValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object" && value.content !== undefined) return String(value.content);
  if (Array.isArray(value) || typeof value === "object") {
    try {
      const json = JSON.stringify(value, null, 2);

      return (
        <pre style={{
          margin: 0, whiteSpace: "pre-wrap", fontSize: "11px",
          background: "#f5f5f5", padding: "4px 6px", borderRadius: "4px"
        }}>
          <code>{json}</code>
        </pre>
      );
    } catch { return "[object]"; }
  }

  return String(value);
};

/** Extracts "general" fields from a TravelAnalytics record (excludes content, processing_result, vehicleState, alert, event). */
const extractGeneralFields = (record: TravelAnalytics): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { content, processing_result, vehicleState, alert, event, ...rest } = record as any;

  return rest;
};

const CommandDetailDialog: React.FC<CommandDetailDialogProps> = ({ detail, onClose }) => {
  const { t } = useTranslation();

  if (!detail) return null;

  const { record, tab } = detail;

  let data: Record<string, any> | null = null;

  switch (tab) {
  case "general":
    data = extractGeneralFields(record);
    break;
  case "content":
    data = record.content as any;
    break;
  case "result":
    data = (record as any).processing_result || null;
    break;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth style={{ zIndex: 2100 }}>
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{t(TAB_TITLES[tab])}</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers style={{ maxHeight: "60vh", overflow: "auto", padding: "8px 16px" }}>
        {renderObjectTable(data, t)}
      </DialogContent>
    </Dialog>
  );
};

// endregion

export default TravelCoordinateViewModal;
